import { h } from "preact";
import { RoutableProps } from "preact-router";
import { useCallback, useEffect, useState } from "preact/hooks";
import { addProductToDatabase, getLabel, getSerialNumberInfo, Label } from "../../../api/product-api";
import { Alert } from "../../../components/Alert";
import { QRCode } from "../../../components/QRCode";
import { ScanPackQR } from "../../../components/ScanPackQR";
import { Snackbar, SnackbarShowCommand } from "../../../components/Snackbar";
import { TemplatePrint } from "../../../components/TemplatePrint";
import { useCommand } from "../../../hooks";
import "./styles.scss";

const defaultProvisionConfig = {
    "android.app.extra.PROVISIONING_DEVICE_ADMIN_COMPONENT_NAME": "com.procoit.kiosklauncher/com.procoit.kiosklauncher.receiver.AdminReceiver",
    // "android.app.extra.PROVISIONING_DEVICE_ADMIN_PACKAGE_DOWNLOAD_LOCATION": "https://klremote.blob.core.windows.net/apk/current/com.procoit.kiosklauncher.apk",
    // "android.app.extra.PROVISIONING_DEVICE_ADMIN_PACKAGE_CHECKSUM": "IL9M94KF6mXdrsZtvSWclx3goed2SFcPMqgZai3JxHM=",
    "android.app.extra.PROVISIONING_DEVICE_ADMIN_PACKAGE_DOWNLOAD_LOCATION": "https://cdn.egzotech.com/kiosklauncher/kiosklauncher-1-5-7-build-20152.apk",
    "android.app.extra.PROVISIONING_DEVICE_ADMIN_PACKAGE_CHECKSUM": "F7VFeS2cILVM2gIwb5YBKjVCdrxpnJt1KhopzDLIYDg=",
    "android.app.extra.EXTRA_PROVISIONING_LOCALE": "pl_PL",
    "android.app.extra.PROVISIONING_LEAVE_ALL_SYSTEM_APPS_ENABLED": true,
    "android.app.extra.PROVISIONING_ADMIN_EXTRAS_BUNDLE": {
        "registration_key": "",
        "licence_key": "6AVI6L", // TODO: add choosing/updating licence key in the future
        "automatic_config_url": "https://production.internal.egzotech.com/api/kiosk/config?pass=1234&env=test",
        "apk_urls": "",
        "file_urls": "https://cdn.egzotech.com/tablet-kiosk/Hotspot.flo,https://production.internal.egzotech.com/api/kiosk/wallpaper/wallpaper.jpeg?sn=ABCDEFGH&wifi=StellaBIO-1234&pass=12345678"
    }
};

async function toBase64(data: BlobPart) {
    // Use a FileReader to generate a base64 data URI
    const base64url = await new Promise<string>((r) => {
        const reader = new FileReader()
        reader.onload = () => r(reader.result as string)
        reader.readAsDataURL(new Blob([data]))
    })

    /*
    The result looks like
    "data:application/octet-stream;base64,<your base64 data>",
    so we split off the beginning:
    */
    return base64url.split(",", 2)[1]
}


async function generateServiceData(serialNumber: string) {
    const serialSha = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(serialNumber));
    const cleanedUpSerialNumber = serialNumber.replace(/[^a-zA-Z0-9]/g, "");
    const hotspot = `StellaBIO-${cleanedUpSerialNumber.slice(cleanedUpSerialNumber.length - 8, cleanedUpSerialNumber.length)}`;
    const serialNumberBase64 = await toBase64(serialSha);
    const hotspotPassword = serialNumberBase64.replace(/[^a-zA-Z0-9]/g, "").slice(0, 8);

    return {
        password: Array.from(new Uint8Array(serialSha))
            .map((v) => v.toString(16).padStart(2, "0"))
            .join("")
            .replace(/[^0-9]/g, "")
            .slice(0, 8),
        hotspot,
        hotspotPassword
    };
}

async function generateTabletQRCode(serialNumber: string, environment: string = "prod") {
    if (!serialNumber) {
        throw new Error("Invalid serial number");
    }

    const serviceData = await generateServiceData(serialNumber);

    const copy = JSON.parse(JSON.stringify(defaultProvisionConfig)) as typeof defaultProvisionConfig;
    copy["android.app.extra.EXTRA_PROVISIONING_LOCALE"] = "pl_PL";
    copy["android.app.extra.PROVISIONING_ADMIN_EXTRAS_BUNDLE"].automatic_config_url = `https://production.internal.egzotech.com/api/kiosk/config?pass=${serviceData.password}&env=${environment}`;
    copy["android.app.extra.PROVISIONING_ADMIN_EXTRAS_BUNDLE"].file_urls = [
        "https://cdn.egzotech.com/tablet-kiosk/Hotspot.flo",
        `https://production.internal.egzotech.com/api/kiosk/wallpaper/wallpaper-LTE.jpeg?sn=${encodeURIComponent(serialNumber)}&wifi=${serviceData.hotspot}&pass=${serviceData.hotspotPassword}`,
        `https://production.internal.egzotech.com/api/kiosk/wallpaper/wallpaper-WiFi.jpeg?sn=${encodeURIComponent(serialNumber)}`
    ].join(",");

    return JSON.stringify(copy);
}

export function StellaBIOInstallTablet(props: RoutableProps) {
    const [tabletType, setTabletType] = useState("ACC-Tablet-Lenovo-Tab-P11-5G");
    const [serialNumber, setSerialNumber] = useState("");
    const [serialNumberStatus, setSerialNumberStatus] = useState("unknown" as "unknown" | "new" | "existing");
    const [environment, setEnvironment] = useState("prod");
    const [advancedEnvironment, setAdvancedEnvironment] = useState(false);
    const [serviceData, setServiceData] = useState(null as { password: string, hotspot: string, hotspotPassword: string } | null);
    const [config, setConfig] = useState("");
    const [serviceLabel, setServiceLabel] = useState(null as Label | null);

    const showSnackbar = useCommand<SnackbarShowCommand>();

    const updateSerialNumber = useCallback(async (serialNumber: string) => {
        serialNumber = serialNumber.toUpperCase();
        setSerialNumber(serialNumber);
        setConfig("");
        setSerialNumberStatus("unknown");

        if (!serialNumber || serialNumber.length < 8) {
            showSnackbar.run("Nieprawidłowy numer seryjny.", "error", "error", 2000)
            return;
        }

        getSerialNumberInfo([serialNumber]).then(([v]) => {
            setSerialNumberStatus(v ? "existing" : "new");
        });

        setServiceData(await generateServiceData(serialNumber));
    }, []);

    useEffect(() => {
        getLabel(109).then((v) => setServiceLabel(v));
    }, []);

    return <div class="stella-bio-install-tablet-page">
        <Snackbar doShow={showSnackbar}/>
        <div style="display: flex; flex-direction: column">
            <div style="display: flex;">
                <div class="paper" style="padding: 10px;">
                    <h1>Zarejestruj tablet i wygeneruj kod QR do instalacji</h1>
                    <ScanPackQR onRead={(data) => {
                        const text = JSON.parse(data) as string;
                        updateSerialNumber(text);
                    }}/>
                    <div>
                        <span style="display: inline-block; width: 140px">Rodzaj tabletu</span>
                        <select style="margin-left: 10px" value={tabletType} onChange={(e) => {
                            setTabletType(e.currentTarget.value)
                            setConfig("");
                        }}>
                            <option value="ACC-Tablet-Lenovo-Tab-P11-5G">Lenovo Tab P11 5G</option>
                        </select>
                    </div>
                    <div style="margin-top: 10px; margin-bottom: 10px;">
                        <span style="display: inline-block; width: 140px">Numer seryjny tabletu</span>
                        <input style="margin-left: 10px; width: 200px" value={serialNumber} type="text" onChange={async (e) => {
                            updateSerialNumber(e.currentTarget.value);
                        }}></input>
                    </div>
                    {serialNumberStatus === "existing" ? <Alert type="success">Tablet został zarejestrowany w systemie</Alert>
                        : serialNumberStatus === "new" ? <Alert type="warning">Tablet nie jest zarejestrowany w systemie</Alert>
                        : null}
                    <div style="margin-top: 10px;">
                        <span style="display: inline-block; width: 140px" onClick={(e) => e.detail >= 3 && setAdvancedEnvironment(true)}>Wersja</span>
                        <select style="margin-left: 10px" value={environment} onChange={(e) => {
                            setEnvironment(e.currentTarget.value)
                            setConfig("");
                        }}>
                            <option value="prod">Stabilna</option>
                            <option value="beta">Beta</option>
                            {advancedEnvironment ? [
                                <option value="test">Testowa</option>,
                                <option value="dev">Deweloperska</option>
                            ] : null}
                        </select>
                    </div>
                    <div style="margin-top: 10px;">
                        <span style="display: inline-block; width: 140px">Kod serwisowy:</span>
                        <span style="margin-left: 10px">{serviceData?.password}</span>
                    </div>
                    <div style="margin-top: 10px;">
                        <span style="display: inline-block; width: 140px">Nazwa HotSpot:</span>
                        <span style="margin-left: 10px">{serviceData?.hotspot}</span>
                    </div>
                    <div style="margin-top: 10px;">
                        <span style="display: inline-block; width: 140px">Hasło HotSpot:</span>
                        <span style="margin-left: 10px">
                            <code style="font-family: 'Roboto Mono'">{serviceData?.hotspotPassword}</code>
                        </span>
                    </div>
                    <button class={serialNumberStatus === "unknown" ? "disabled" : ""} style="margin-top: 10px;" onClick={async () => {
                        if (serialNumberStatus === "new") {
                            await addProductToDatabase(tabletType, serialNumber);
                            showSnackbar.run("Pomyślnie zarejestrowano tablet", "success", "check", 2000);
                            updateSerialNumber(serialNumber);
                        }
                        setConfig(await generateTabletQRCode(serialNumber, environment));
                    }}>{serialNumberStatus === "new" ? "Zarejestruj tablet i wygeneruj QR kod" : "Wygeneruj QR kod"}</button>
                </div>
                {config ? <div class="paper" style="padding: 10px; margin-left: 10px; flex: 1 0">
                    {serviceLabel ?
                        <TemplatePrint
                            template={serviceLabel.path}
                            fields={{
                                "field-kod": serviceData?.password ?? "",
                                "field-nazwa_hotspot": serviceData?.hotspot ?? "",
                                "field-haslo_hotspot": serviceData?.hotspotPassword ?? ""
                            }}
                            overrideFields={serviceLabel.metadata?.metadata}
                        />
                    : null}
                </div> : null}
            </div>
            {config ? <div style="background: white; padding: 40px 20px; margin-top: 10px; display: flex; align-items: center; justify-content: center;">
                <QRCode data={config}/>
            </div> : null}
        </div>

    </div>;
}
